import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Checkbox } from 'components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { CityAutocomplete } from 'components/CityAutocomplete';
import { CountryAutocomplete } from 'components/CountryAutocomplete';
import { ParcelTypeCheckboxes } from 'components/ParcelTypeCheckboxes';
import api, { getApiErrorMessage } from 'lib/api';
import { toast } from 'sonner';
import { TRANSPORT_LABELS } from 'lib/format';
import { AlertTriangle, Loader2, Plane } from 'lucide-react';
import { useAuth } from 'contexts/AuthContext';
import { useLanguage } from 'lib/i18n';

export default function CreateTripPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    departure_country: '', departure_city: '',
    departure_lat: null, departure_lng: null, arrival_country: '', arrival_city: '', arrival_lat: null, arrival_lng: null,
    intermediate_cities: '',
    departure_date: '', arrival_date: '',
    transport_mode: 'plane',
    capacity_kg: 10, max_parcels: 5,
    dropoff_deadline_hours: '', availability_start: '', availability_end: '',
    max_dimensions_cm: '',
    accepted_parcel_types: ['documents', 'small'],
    restrictions: '',
    price_per_kg: '', flat_price: '', negotiable: true,
    notes: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/trips', {
        ...form,
        capacity_kg: Number(form.capacity_kg),
        max_parcels: Number(form.max_parcels),
        dropoff_deadline_hours: form.dropoff_deadline_hours ? Number(form.dropoff_deadline_hours) : null,
        availability_windows: form.availability_start && form.availability_end ? [{ start: form.availability_start, end: form.availability_end }] : [],
        price_per_kg: form.price_per_kg ? Number(form.price_per_kg) : null,
        flat_price: form.flat_price ? Number(form.flat_price) : null,
        intermediate_cities: form.intermediate_cities ? form.intermediate_cities.split(',').map(s => s.trim()).filter(Boolean) : [],
      });
      toast.success(t('trip.published'));
      navigate('/trips');
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('trip.error')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardShell>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Plane className="text-primary" /> {t('trip.createTitle')}
        </h1>
        <p className="text-muted-foreground">{t('trip.createSubtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-3xl">
        {!user?.is_verified_id && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('delivery.verifyTitle')}</AlertTitle>
            <AlertDescription>{t('trip.verifyText')}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader><CardTitle className="text-base">{t('trip.route')}</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{t('trip.departureCountry')}</Label>
              <CountryAutocomplete
                required
                value={form.departure_country}
                onChange={value => set('departure_country', value)}
                onSelect={country => setForm(current => ({ ...current, departure_country: country, departure_city: '', departure_lat: null, departure_lng: null }))}
                testId="trip-dep-country-input"
              />
            </div>
            <div>
              <Label>{t('trip.departureCity')}</Label>
              <CityAutocomplete
                required
                value={form.departure_city}
                country={form.departure_country}
                onChange={value => set('departure_city', value)}
                onSelect={({ city, country, latitude, longitude }) => setForm(current => ({ ...current, departure_city: city, departure_country: country, departure_lat: latitude, departure_lng: longitude }))}
                placeholder="Paris"
                testId="trip-dep-city-input"
              />
            </div>
            <div>
              <Label>{t('trip.arrivalCountry')}</Label>
              <CountryAutocomplete
                required
                value={form.arrival_country}
                onChange={value => set('arrival_country', value)}
                onSelect={country => setForm(current => ({ ...current, arrival_country: country, arrival_city: '', arrival_lat: null, arrival_lng: null }))}
                placeholder={t('trip.arrivalCountryPlaceholder')}
                testId="trip-arr-country-input"
              />
            </div>
            <div>
              <Label>{t('trip.arrivalCity')}</Label>
              <CityAutocomplete
                required
                value={form.arrival_city}
                country={form.arrival_country}
                onChange={value => set('arrival_city', value)}
                onSelect={({ city, country, latitude, longitude }) => setForm(current => ({ ...current, arrival_city: city, arrival_country: country, arrival_lat: latitude, arrival_lng: longitude }))}
                placeholder="Dakar"
                testId="trip-arr-city-input"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>{t('trip.stops')}</Label>
              <Input value={form.intermediate_cities} onChange={e => set('intermediate_cities', e.target.value)} placeholder={t('trip.stopsPlaceholder')} />
            </div>
            <div>
              <Label>{t('trip.departureDate')}</Label>
              <Input type="datetime-local" required value={form.departure_date} onChange={e => set('departure_date', e.target.value)} data-testid="trip-departure-date-input" />
            </div>
            <div>
              <Label>{t('trip.arrivalDate')}</Label>
              <Input type="datetime-local" value={form.arrival_date} onChange={e => set('arrival_date', e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">{t('trip.capacity')}</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{t('trip.mode')}</Label>
              <Select value={form.transport_mode} onValueChange={v => set('transport_mode', v)}>
                <SelectTrigger data-testid="trip-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TRANSPORT_LABELS).filter(([k]) => ['car','bus','train','plane','boat','motorcycle','truck'].includes(k)).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('trip.capacityKg')}</Label>
              <Input type="number" min={1} required value={form.capacity_kg} onChange={e => set('capacity_kg', e.target.value)} data-testid="trip-capacity-input" />
            </div>
            <div>
              <Label>{t('trip.maxParcels')}</Label>
              <Input type="number" min={1} value={form.max_parcels} onChange={e => set('max_parcels', e.target.value)} />
            </div>
            <div>
              <Label>{t('trip.maxDimensions')}</Label>
              <Input value={form.max_dimensions_cm} onChange={e => set('max_dimensions_cm', e.target.value)} placeholder="50x40x20" />
            </div>
            <div>
              <Label>{t('trip.deadline')}</Label>
              <Input type="number" min={0} value={form.dropoff_deadline_hours} onChange={e => set('dropoff_deadline_hours', e.target.value)} placeholder="48" />
            </div>
            <div className="sm:col-span-2 grid sm:grid-cols-2 gap-4">
              <div><Label>{t('trip.availableFrom')}</Label><Input type="datetime-local" value={form.availability_start} onChange={e => set('availability_start', e.target.value)} /></div>
              <div><Label>{t('trip.availableUntil')}</Label><Input type="datetime-local" value={form.availability_end} onChange={e => set('availability_end', e.target.value)} /></div>
            </div>
            <div className="sm:col-span-2">
              <ParcelTypeCheckboxes legend={t('trip.acceptedTypes')} value={form.accepted_parcel_types} onChange={value => set('accepted_parcel_types', value)} />
            </div>
            <div className="sm:col-span-2">
              <Label>{t('trip.restrictions')}</Label>
              <Textarea value={form.restrictions} onChange={e => set('restrictions', e.target.value)} placeholder={t('trip.restrictionsPlaceholder')} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">{t('trip.price')}</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{t('trip.pricePerKg')}</Label>
              <Input type="number" min={0} step={0.5} value={form.price_per_kg} onChange={e => set('price_per_kg', e.target.value)} placeholder="5" data-testid="trip-price-input" />
            </div>
            <div>
              <Label>{t('trip.flatPrice')}</Label>
              <Input type="number" min={0} value={form.flat_price} onChange={e => set('flat_price', e.target.value)} />
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <Checkbox id="neg" checked={form.negotiable} onCheckedChange={v => set('negotiable', v)} />
              <Label htmlFor="neg" className="cursor-pointer">{t('trip.negotiable')}</Label>
            </div>
            <div className="sm:col-span-2">
              <Label>{t('trip.notes')}</Label>
              <Textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder={t('trip.notesPlaceholder')} />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>{t('trip.cancel')}</Button>
          <Button type="submit" disabled={loading || !user?.is_verified_id} data-testid="trip-submit-button">
            {loading ? <Loader2 className="animate-spin" size={16} /> : t('trip.publish')}
          </Button>
        </div>
      </form>
    </DashboardShell>
  );
}
